import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Select, Spin, Typography, Tag, Empty } from 'antd';
import { DatabaseOutlined, ClockCircleOutlined, WarningOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { getQueryStats, getDataSources, getTableUsage, getQueryTimeline } from '../services/adminService';

const { Title, Text } = Typography;

function DataUsage() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [tables, setTables] = useState([]);
  const [timeline, setTimeline] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, src, tbl, tl] = await Promise.all([
        getQueryStats(days),
        getDataSources(days),
        getTableUsage(days),
        getQueryTimeline(days),
      ]);
      setStats(s);
      setSources(src.sources || []);
      setTables(tbl.tables || []);
      setTimeline(tl.timeline || []);
    } catch (err) {
      console.error('Failed to load data usage:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [days]); // eslint-disable-line

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const sourceColumns = [
    { title: 'Data Source', dataIndex: 'data_source', key: 'data_source' },
    { title: 'Query Count', dataIndex: 'query_count', key: 'query_count', sorter: (a, b) => a.query_count - b.query_count, defaultSortOrder: 'descend' },
    { title: 'Avg Time (ms)', dataIndex: 'avg_time_ms', key: 'avg_time_ms', render: v => v?.toLocaleString() },
  ];

  const tableColumns = [
    { title: 'Schema', dataIndex: 'schema', key: 'schema' },
    { title: 'Table/View', dataIndex: 'table', key: 'table' },
    { title: 'Query Count', dataIndex: 'query_count', key: 'query_count', sorter: (a, b) => a.query_count - b.query_count, defaultSortOrder: 'descend' },
    { title: 'Avg Time (ms)', dataIndex: 'avg_time_ms', key: 'avg_time_ms', render: v => v?.toLocaleString() },
  ];

  const timelineColumns = [
    { title: 'Date', dataIndex: 'date', key: 'date' },
    { title: 'Queries', dataIndex: 'queries', key: 'queries' },
    { title: 'Avg Time (ms)', dataIndex: 'avg_time_ms', key: 'avg_time_ms', render: v => v?.toLocaleString() },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}><DatabaseOutlined /> Data Usage Audit</Title>
        <Select value={days} onChange={setDays} style={{ width: 150 }}
          options={[{ value: 7, label: 'Last 7 days' }, { value: 30, label: 'Last 30 days' }, { value: 90, label: 'Last 90 days' }, { value: 365, label: 'Last year' }]} />
      </div>

      <Spin spinning={loading}>
        {stats && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}><Card><Statistic title="Total Queries" value={stats.total_queries} prefix={<DatabaseOutlined />} /></Card></Col>
            <Col span={6}><Card><Statistic title="Avg Duration" value={stats.avg_duration_ms} suffix="ms" prefix={<ClockCircleOutlined />} /></Card></Col>
            <Col span={6}><Card><Statistic title="Slow Queries (≥60s)" value={stats.slow_queries} prefix={<WarningOutlined />} valueStyle={stats.slow_queries > 0 ? { color: '#cf1322' } : {}} /></Card></Col>
            <Col span={6}><Card><Statistic title="Data Transfer" value={formatBytes(stats.total_data_bytes)} prefix={<CloudDownloadOutlined />} /></Card></Col>
          </Row>
        )}

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Card title="Data Sources" size="small">
              {sources.length ? <Table columns={sourceColumns} dataSource={sources} rowKey="data_source" size="small" pagination={false} /> : <Empty description="No data" />}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Top Tables/Views" size="small">
              {tables.length ? <Table columns={tableColumns} dataSource={tables} rowKey={r => `${r.schema}-${r.table}`} size="small" pagination={{ pageSize: 10 }} /> : <Empty description="No data" />}
            </Card>
          </Col>
        </Row>

        <Card title="Query Timeline">
          <Table columns={timelineColumns} dataSource={timeline} rowKey="date" size="small" pagination={{ pageSize: 10 }} />
        </Card>
      </Spin>
    </div>
  );
}

export default DataUsage;
